// ============================================
// RAG Pipeline Playground – UI logic
// ============================================

import {
  chunkByCharacter,
  chunkByParagraph,
  computeStats,
  findSimilar,
  SAMPLE_TEXTS,
  type Chunk,
  type ChunkConfig,
  type ChunkStats,
  type SimilarityResult,
} from '../../lib/labs/rag-playground';

const root = document.querySelector('[data-rag-playground]') as HTMLElement | null;

if (root) {
  // ============================================
  // DOM Elements
  // ============================================
  const textInput = root.querySelector('#rp-text-input') as HTMLTextAreaElement;
  const sampleSelect = root.querySelector('#rp-sample-select') as HTMLSelectElement;
  const chunkSizeSlider = root.querySelector('#rp-chunk-size') as HTMLInputElement;
  const chunkSizeValue = root.querySelector('#rp-chunk-size-value') as HTMLElement;
  const overlapSlider = root.querySelector('#rp-overlap') as HTMLInputElement;
  const overlapValue = root.querySelector('#rp-overlap-value') as HTMLElement;
  const modeCharacter = root.querySelector('#rp-mode-character') as HTMLInputElement;
  const modeParagraph = root.querySelector('#rp-mode-paragraph') as HTMLInputElement;
  const overlapGroup = root.querySelector('[data-rp-overlap-group]') as HTMLElement;
  const chunkBtn = root.querySelector('#rp-chunk-btn') as HTMLButtonElement;

  const chunkVisualization = root.querySelector('#rp-chunk-visualization') as HTMLElement;
  const chunkList = root.querySelector('#rp-chunk-list') as HTMLElement;

  const statsCount = root.querySelector('#rp-stats-count') as HTMLElement;
  const statsAvg = root.querySelector('#rp-stats-avg') as HTMLElement;
  const statsRange = root.querySelector('#rp-stats-range') as HTMLElement;
  const statsOverlap = root.querySelector('#rp-stats-overlap') as HTMLElement;

  const queryInput = root.querySelector('#rp-query-input') as HTMLInputElement;
  const searchBtn = root.querySelector('#rp-search-btn') as HTMLButtonElement;
  const searchResults = root.querySelector('#rp-search-results') as HTMLElement;

  // ============================================
  // State
  // ============================================
  let currentChunks: Chunk[] = [];

  // Chunk colors (cycle through for visual distinction)
  const CHUNK_COLORS = [
    { bg: 'rgba(99, 102, 241, 0.15)', border: 'rgb(99, 102, 241)' },
    { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgb(16, 185, 129)' },
    { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgb(245, 158, 11)' },
    { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgb(239, 68, 68)' },
    { bg: 'rgba(139, 92, 246, 0.15)', border: 'rgb(139, 92, 246)' },
    { bg: 'rgba(6, 182, 212, 0.15)', border: 'rgb(6, 182, 212)' },
    { bg: 'rgba(236, 72, 153, 0.15)', border: 'rgb(236, 72, 153)' },
    { bg: 'rgba(234, 179, 8, 0.15)', border: 'rgb(234, 179, 8)' },
  ];

  // ============================================
  // Event Handlers
  // ============================================

  sampleSelect.addEventListener('change', () => {
    const key = sampleSelect.value as keyof typeof SAMPLE_TEXTS;
    if (key !== 'custom') {
      textInput.value = SAMPLE_TEXTS[key];
    } else {
      textInput.value = '';
      textInput.focus();
    }
    clearResults();
  });

  chunkSizeSlider.addEventListener('input', () => {
    chunkSizeValue.textContent = chunkSizeSlider.value;
    // Keep overlap below chunk size
    const maxOverlap = Math.min(
      parseInt(overlapSlider.value),
      parseInt(chunkSizeSlider.value) - 1
    );
    if (parseInt(overlapSlider.value) > maxOverlap) {
      overlapSlider.value = String(maxOverlap);
      overlapValue.textContent = String(maxOverlap);
    }
    overlapSlider.max = String(parseInt(chunkSizeSlider.value) - 1);
  });

  overlapSlider.addEventListener('input', () => {
    overlapValue.textContent = overlapSlider.value;
  });

  function updateOverlapVisibility() {
    if (modeParagraph.checked) {
      overlapGroup.style.opacity = '0.4';
      overlapSlider.disabled = true;
      overlapValue.textContent = 'N/A';
    } else {
      overlapGroup.style.opacity = '';
      overlapSlider.disabled = false;
      overlapValue.textContent = overlapSlider.value;
    }
  }

  modeCharacter.addEventListener('change', updateOverlapVisibility);
  modeParagraph.addEventListener('change', updateOverlapVisibility);

  chunkBtn.addEventListener('click', runChunking);

  searchBtn.addEventListener('click', runSearch);

  queryInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') runSearch();
  });

  // ============================================
  // Core Logic
  // ============================================

  function runChunking() {
    const text = textInput.value.trim();
    if (!text) return;

    const config: ChunkConfig = {
      size: parseInt(chunkSizeSlider.value),
      overlap: parseInt(overlapSlider.value),
    };

    const isParagraphMode = modeParagraph.checked;

    if (!isParagraphMode) {
      currentChunks = chunkByCharacter(text, config);
    } else {
      currentChunks = chunkByParagraph(text, config.size);
    }

    const statsConfig = isParagraphMode ? { ...config, overlap: 0 } : config;
    renderVisualization(text, currentChunks);
    renderChunkList(currentChunks);
    renderStats(computeStats(currentChunks, statsConfig));
    searchResults.innerHTML = '';
  }

  function runSearch() {
    const query = queryInput.value.trim();
    if (!query || currentChunks.length === 0) return;

    const results = findSimilar(query, currentChunks, 3);
    renderSearchResults(results);
  }

  function clearResults() {
    currentChunks = [];
    chunkVisualization.innerHTML =
      '<p class="text-sm text-muted italic">Paste text and click "Chunk" to visualize</p>';
    chunkList.innerHTML = '';
    searchResults.innerHTML = '';
    statsCount.textContent = '0';
    statsAvg.textContent = '0';
    statsRange.textContent = '0–0';
    statsOverlap.textContent = '0%';
  }

  // ============================================
  // Rendering
  // ============================================

  function renderVisualization(originalText: string, chunks: Chunk[]) {
    if (chunks.length === 0) {
      chunkVisualization.innerHTML =
        '<p class="text-sm text-muted italic">No chunks produced</p>';
      return;
    }

    // Build colored spans from chunks
    const fragments: string[] = [];
    let lastEnd = 0;

    for (const chunk of chunks) {
      // Gap between chunks (if any)
      if (chunk.start > lastEnd) {
        const gap = escapeHtml(originalText.slice(lastEnd, chunk.start));
        fragments.push(`<span class="text-muted/50">${gap}</span>`);
      }

      const color = CHUNK_COLORS[chunk.index % CHUNK_COLORS.length];
      const text = escapeHtml(chunk.text);
      fragments.push(
        `<span class="rp-chunk-span" data-chunk="${chunk.index}" style="background:${color.bg};border-bottom:2px solid ${color.border};cursor:pointer;" title="Chunk ${chunk.index} (${chunk.text.length} chars)">${text}</span>`
      );
      lastEnd = chunk.end;
    }

    // Any remaining text
    if (lastEnd < originalText.length) {
      fragments.push(
        `<span class="text-muted/50">${escapeHtml(originalText.slice(lastEnd))}</span>`
      );
    }

    chunkVisualization.innerHTML = `<pre class="whitespace-pre-wrap break-words text-sm leading-relaxed text-text font-mono">${fragments.join('')}</pre>`;

    // Add hover interaction
    const spans = chunkVisualization.querySelectorAll('.rp-chunk-span');
    spans.forEach((span) => {
      span.addEventListener('mouseenter', () => {
        const idx = parseInt((span as HTMLElement).dataset.chunk ?? '0');
        highlightChunkInList(idx);
      });
      span.addEventListener('mouseleave', () => {
        clearChunkHighlight();
      });
    });
  }

  function renderChunkList(chunks: Chunk[]) {
    if (chunks.length === 0) {
      chunkList.innerHTML = '';
      return;
    }

    const items = chunks.map((chunk) => {
      const color = CHUNK_COLORS[chunk.index % CHUNK_COLORS.length];
      const preview =
        chunk.text.length > 120 ? chunk.text.slice(0, 120) + '...' : chunk.text;
      return `
        <div class="rp-chunk-item rounded-lg border border-border/70 p-3 transition" data-chunk-item="${chunk.index}" style="border-left:3px solid ${color.border}">
          <div class="flex items-center justify-between">
            <span class="text-xs font-medium text-text">Chunk ${chunk.index}</span>
            <span class="text-xs text-muted">${chunk.text.length} chars · pos ${chunk.start}–${chunk.end}</span>
          </div>
          <p class="mt-1 text-xs text-muted font-mono break-words">${escapeHtml(preview)}</p>
        </div>
      `;
    });

    chunkList.innerHTML = items.join('');
  }

  function renderStats(stats: ChunkStats) {
    statsCount.textContent = String(stats.count);
    statsAvg.textContent = String(stats.avgLength);
    statsRange.textContent = `${stats.minLength}–${stats.maxLength}`;
    statsOverlap.textContent = `${stats.overlapPercent}%`;
  }

  function renderSearchResults(results: SimilarityResult[]) {
    if (results.length === 0) {
      searchResults.innerHTML = '<p class="text-sm text-muted italic">No results</p>';
      return;
    }

    const items = results.map((r, i) => {
      const color = CHUNK_COLORS[r.chunk.index % CHUNK_COLORS.length];
      const score = (r.score * 100).toFixed(1);
      const preview =
        r.chunk.text.length > 150 ? r.chunk.text.slice(0, 150) + '...' : r.chunk.text;

      return `
        <div class="rounded-lg border border-border/70 p-3" style="border-left:3px solid ${color.border}">
          <div class="flex items-center justify-between">
            <span class="text-xs font-medium text-text">#${i + 1} — Chunk ${r.chunk.index}</span>
            <span class="text-xs font-medium" style="color:${color.border}">Score: ${score}%</span>
          </div>
          <p class="mt-1 text-xs text-muted font-mono break-words">${escapeHtml(preview)}</p>
        </div>
      `;
    });

    searchResults.innerHTML = items.join('');
  }

  function highlightChunkInList(index: number) {
    const item = chunkList.querySelector(
      `[data-chunk-item="${index}"]`
    ) as HTMLElement | null;
    if (item) {
      item.style.backgroundColor = 'var(--color-surface)';
    }
  }

  function clearChunkHighlight() {
    const items = chunkList.querySelectorAll('.rp-chunk-item');
    items.forEach((item) => {
      (item as HTMLElement).style.backgroundColor = '';
    });
  }

  function escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ============================================
  // Init: load default sample
  // ============================================
  textInput.value = SAMPLE_TEXTS['security-advisory'];
}
